import { OtsDateTime } from '../components/OtsDateTime';
import { useState, type ReactNode } from 'react';
import type {
  RrcEp10Document,
  RrcEp10WasteManifestBuildStatus,
  ComplianceExportFormat,
} from '@ots-frontend/ots-data';
import { AdminPanel } from '../components/AdminPanel';
import { OtsActionButton } from '../components/OtsButton';
import {
  DescriptionItem,
  DescriptionList,
} from '../components/DescriptionList';
import { OtsInlineAlert } from '../components/OtsInlineAlert';
import { OtsMasterDetail } from '../components/OtsMasterDetail';
import { OtsDataTableEmpty } from '../components/OtsDataTableCells';
import {
  OtsDataTable,
  OtsDataTableHead,
  OtsDataTableBody,
  OtsDataTableRow,
  OtsDataTableHeaderCell,
  OtsDataTableCell,
} from '../components/OtsDataTable';
import { OtsTablePanel } from '../components/OtsTablePanel';
import { ComplianceStatusBadge } from './ComplianceStatusBadge';
import { RrcEp10ManifestDetails } from './RrcEp10ManifestDetails';

export type ComplianceResource<T> = {
  data?: T;
  loading?: boolean;
  error?: string;
};
export type RrcEp10ComplianceViewProps = {
  status: ComplianceResource<RrcEp10WasteManifestBuildStatus | null>;
  manifest: ComplianceResource<RrcEp10Document | null>;
  versions: ComplianceResource<RrcEp10Document[]>;
  onDownload?: (
    manifest: RrcEp10Document,
    format: ComplianceExportFormat
  ) => void;
  downloading?: boolean;
  downloadError?: string;
  onRetry?: () => void;
  compact?: boolean;
  onOpenCompanyProfile?: (id: string) => void;
  onOpenLocationProfile?: (id: string) => void;
  onOpenWasteProfile?: (id: string) => void;
  renderPdf?: (manifest: RrcEp10Document) => ReactNode;
};

export const RrcEp10ComplianceView = ({
  status,
  manifest,
  versions,
  onDownload,
  downloading,
  downloadError,
  onRetry,
  compact,
  onOpenCompanyProfile,
  onOpenLocationProfile,
  onOpenWasteProfile,
  renderPdf,
}: RrcEp10ComplianceViewProps) => {
  const [selectedId, selectVersion] = useState<string>();
  const history = [...(versions.data ?? [])].sort(
    (a, b) => b.version - a.version
  );
  const selected =
    history.find((version) => version.id === selectedId) ??
    manifest.data ??
    history[0];
  const size = compact ? 'compact' : 'default';
  const failure = (title: string, message: string) => (
    <OtsInlineAlert
      tone="danger"
      title={title}
      size={size}
      action={
        onRetry && (
          <OtsActionButton size="compact" onClick={onRetry}>
            Try again
          </OtsActionButton>
        )
      }
    >
      {message}
    </OtsInlineAlert>
  );
  return (
    <>
      <AdminPanel
        compact={compact}
        title="Texas RRC · EP-10"
        description="Current build status, independent of the selected document version."
        bodyClassName="@container p-4"
      >
        {status.loading ? (
          <OtsDataTableEmpty loading loadingText="Loading processing status…">
            {null}
          </OtsDataTableEmpty>
        ) : status.error ? (
          failure('Processing status unavailable', status.error)
        ) : !status.data ? (
          <OtsInlineAlert title="Awaiting processing" size={size}>
            No build status has been recorded for this record.
          </OtsInlineAlert>
        ) : (
          <DescriptionList compact={compact} responsive="container">
            <DescriptionItem label="Processing status">
              <ComplianceStatusBadge
                status={status.data.status}
                compact={compact}
              />
            </DescriptionItem>
            <DescriptionItem label="Updated">
              <OtsDateTime value={status.data.lastModifiedDttm} showTimeZone />
            </DescriptionItem>
            <DescriptionItem label="Regeneration">
              {status.data.recommendRegeneration
                ? 'Recommended'
                : 'Not recommended'}
            </DescriptionItem>
          </DescriptionList>
        )}
        {!status.error &&
          !status.loading &&
          status.data?.recommendRegeneration && (
            <OtsInlineAlert
              className="mt-4"
              tone="warning"
              title="Document may be out of date"
              size={size}
            >
              Current ticket data differs from the generated manifest.
              Regeneration is recommended.
            </OtsInlineAlert>
          )}
      </AdminPanel>
      <OtsTablePanel
        title="Validation"
        description="Current build results, independent of the selected document version."
        compact={compact}
        fill={false}
      >
        {status.loading ? (
          <OtsDataTableEmpty loading loadingText="Loading validation…">
            {null}
          </OtsDataTableEmpty>
        ) : status.error ? (
          <div className="p-4">
            {failure('Validation unavailable', status.error)}
          </div>
        ) : !status.data ? (
          <OtsDataTableEmpty compact={compact}>
            Validation results have not been recorded.
          </OtsDataTableEmpty>
        ) : !status.data.violations?.length ? (
          <OtsDataTableEmpty compact={compact}>
            No violations reported in the current build.
          </OtsDataTableEmpty>
        ) : (
          <OtsDataTable
            aria-label="Compliance violations"
            density={size}
            contained={false}
          >
            <OtsDataTableHead>
              <OtsDataTableRow>
                {['Severity', 'Message', 'Field'].map((label) => (
                  <OtsDataTableHeaderCell key={label}>
                    {label}
                  </OtsDataTableHeaderCell>
                ))}
              </OtsDataTableRow>
            </OtsDataTableHead>
            <OtsDataTableBody>
              {status.data.violations.map((violation, i) => (
                <OtsDataTableRow key={`${violation.key}-${i}`}>
                  <OtsDataTableCell>
                    <ComplianceStatusBadge
                      status={violation.severity}
                      compact={compact}
                    />
                  </OtsDataTableCell>
                  <OtsDataTableCell className="whitespace-normal break-words">
                    {violation.message}
                  </OtsDataTableCell>
                  <OtsDataTableCell className="break-all">
                    {violation.path ?? '—'}
                  </OtsDataTableCell>
                </OtsDataTableRow>
              ))}
            </OtsDataTableBody>
          </OtsDataTable>
        )}
      </OtsTablePanel>
      {downloadError && failure('Download unavailable', downloadError)}
      <AdminPanel
        compact={compact}
        title="Manifest documents"
        description="Inspect and download a generated version."
        bodyClassName="@container p-4"
      >
        {manifest.error &&
          failure('Latest manifest unavailable', manifest.error)}
        {versions.error && (
          <div className="mb-4">
            {failure('Version history unavailable', versions.error)}
          </div>
        )}
        <OtsMasterDetail
          navigationTitle="Version history"
          navigationLabel="Manifest versions"
          navigationMode="selector"
          items={history.map((version) => ({
            id: version.id,
            label: `Version ${version.version} · ${version.manifestNumber} · ${
              version.status ?? 'Unknown'
            }`,
            ariaLabel: `Version ${version.version}`,
            content: (
              <>
                <p className="text-sm font-semibold text-[var(--ots-secondary)]">
                  Version {version.version}
                </p>
                <p className="my-1 break-all text-xs text-[var(--ots-text-muted)]">
                  {version.manifestNumber}
                </p>
                <ComplianceStatusBadge status={version.status} compact />
                <p className="mt-1 text-xs text-[var(--ots-text-muted)]">
                  <OtsDateTime value={version.createDttm} showTimeZone />
                </p>
              </>
            ),
          }))}
          selectedId={selected?.id}
          onSelect={selectVersion}
          compact={compact}
          hideNavigation={history.length < 2 && !versions.loading}
          navigationState={
            versions.loading ? (
              <OtsDataTableEmpty loading loadingText="Loading versions…">
                {null}
              </OtsDataTableEmpty>
            ) : undefined
          }
        >
          <div className="min-w-0 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h3 className="min-w-0 break-all font-semibold text-[var(--ots-secondary)]">
                {selected
                  ? `${selected.manifestNumber} · Version ${selected.version}`
                  : 'Manifest document'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {(['pdf', 'csv'] as const).map((format) => (
                  <OtsActionButton
                    key={format}
                    size="compact"
                    disabled={!selected || downloading || !onDownload}
                    onClick={() => selected && onDownload?.(selected, format)}
                  >
                    Download {format.toUpperCase()}
                  </OtsActionButton>
                ))}
              </div>
            </div>
            {manifest.loading && !selected ? (
              <OtsDataTableEmpty loading loadingText="Loading manifest…">
                {null}
              </OtsDataTableEmpty>
            ) : selected ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <ComplianceStatusBadge
                    status={selected.status}
                    compact={compact}
                  />
                  {manifest.data?.id !== selected.id && (
                    <span className="text-xs text-[var(--ots-text-muted)]">
                      Historical document
                    </span>
                  )}
                </div>
                <RrcEp10ManifestDetails
                  manifest={selected}
                  compact={compact}
                  onOpenCompanyProfile={onOpenCompanyProfile}
                  onOpenLocationProfile={onOpenLocationProfile}
                  onOpenWasteProfile={onOpenWasteProfile}
                />
                {renderPdf?.(selected)}
              </>
            ) : (
              !manifest.error && (
                <OtsDataTableEmpty compact={compact}>
                  No EP-10 manifest has been generated.
                </OtsDataTableEmpty>
              )
            )}
          </div>
        </OtsMasterDetail>
      </AdminPanel>
    </>
  );
};
